import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  DialogProps,
  DialogActions,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import styled from "@emotion/styled";
import { Form } from "@shared/components/Form";
import { TextField } from "@shared/components/TextField";
import {
  updateSubscriptionTrialDaysFormSchema,
  UpdateSubscriptionTrialDaysFormSchema,
  TRIAL_DAYS_MAX_LENGTH,
} from "./update-subscription-trial-days-helpers";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { SubmitButton } from "@avenue-8/sidekick-ui";
import { format } from "date-fns";
import { FormErrorMessage } from "@shared/components/FormErrorMessage";
import { DatePicker } from "@mui/x-date-pickers";

const CustomDialogTitle = styled(DialogTitle)`
  display: grid;
  grid-template-columns: auto 40px;
  align-items: center;
  padding-right: 1rem;
`;

const StyledDatePicker = styled(DatePicker)({
  "& .MuiInputAdornment-root": {
    marginRight: "1rem",
  },
});

interface Props extends DialogProps {
  userInfo: {
    name: string;
    email: string;
    subscriptionPlan?: {
      name: string;
      trialEndDate: Date | null;
    };
  };
  open: boolean;
  isLoading: boolean;
  onClose: () => void;
  onUpdateSubscriptionTrialDays: (data: {
    trialDays?: number | null;
    trialEndDate?: Date | null;
  }) => void;
}

export const UpdateSubscriptionTrialDaysModal = (props: Props) => {
  const {
    open,
    userInfo,
    isLoading,
    onClose,
    onUpdateSubscriptionTrialDays,
    ...rest
  } = props;

  const {
    register,
    formState: { errors },
    handleSubmit,
    control,
    watch,
  } = useForm<UpdateSubscriptionTrialDaysFormSchema>({
    defaultValues: {
      trialOption: "days",
      trialDays: null,
      trialEndDate: null,
    },
    resolver: yupResolver(updateSubscriptionTrialDaysFormSchema),
    reValidateMode: "onChange",
  });

  const trialOption = watch("trialOption");

  const handleUpdateSubscriptionTrialDays = (
    data: UpdateSubscriptionTrialDaysFormSchema
  ) => {
    if (data.trialOption === "date") {
      onUpdateSubscriptionTrialDays({
        trialEndDate: data.trialEndDate,
      });
      return;
    }
    onUpdateSubscriptionTrialDays({
      trialDays: data.trialDays,
    });
  };

  return (
    <Dialog {...rest} fullWidth maxWidth="sm" open={open}>
      <CustomDialogTitle id="update-subscription-trial-days-dialog-title">
        <span>Update Subscription Trial Days</span>
        <IconButton aria-label="close" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </CustomDialogTitle>
      <DialogContent>
        <Typography mt={2}>
          <strong>Name:</strong> {userInfo.name}
        </Typography>
        <Typography mt={1}>
          <strong>E-mail:</strong> {userInfo.email}
        </Typography>
        <Typography mt={1}>
          <strong>Plan Name:</strong>{" "}
          {userInfo.subscriptionPlan?.name ?? "Unknown"}
        </Typography>
        {!!userInfo.subscriptionPlan?.trialEndDate && (
          <Typography mt={1} mb={4}>
            <strong>Current Trial Ends on:</strong>{" "}
            {format(userInfo.subscriptionPlan.trialEndDate, "MMM d, yyyy")}
          </Typography>
        )}

        <Form
          id="admin-update-subscription-trial-days"
          onSubmit={handleSubmit(handleUpdateSubscriptionTrialDays)}
          aria-disabled={isLoading}
        >
          <FormControl fullWidth>
            <InputLabel id="trial-option-label">
              Select How to Set the Trial Period
            </InputLabel>
            <Controller
              name="trialOption"
              control={control}
              render={({ field }) => (
                <Select
                  label="Trial Option"
                  {...field}
                  error={!!errors.trialOption?.message}
                >
                  <MenuItem value={"days"}>Set Trial Duration in Days</MenuItem>
                  <MenuItem value={"date"}>Set a Fixed Trial End Date</MenuItem>
                </Select>
              )}
            />
            <FormErrorMessage>
              {errors.trialOption?.message ?? " "}
            </FormErrorMessage>
          </FormControl>
          {trialOption === "days" && (
            <TextField
              name={"trialDays"}
              label={"Trial Days"}
              type="number"
              inputProps={{
                ...register("trialDays"),
                min: 0,
                max: TRIAL_DAYS_MAX_LENGTH,
              }}
              fieldError={errors.trialDays}
              autoFocus
            />
          )}
          {trialOption === "date" && (
            <FormControl>
              <InputLabel id="trial-end-date-label">Trial end date</InputLabel>
              <Controller
                name="trialEndDate"
                control={control}
                defaultValue={null}
                render={({ field, fieldState }) => (
                  <StyledDatePicker
                    {...field}
                    value={field.value || null}
                    onChange={(date) => field.onChange(date || null)}
                    minDate={new Date()}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!fieldState.error,
                      },
                    }}
                  />
                )}
              />
              <FormErrorMessage>
                {errors.trialEndDate?.message ?? " "}
              </FormErrorMessage>
            </FormControl>
          )}
        </Form>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="text" disabled={isLoading}>
          Go Back
        </Button>
        <SubmitButton
          form="admin-update-subscription-trial-days"
          isLoading={isLoading}
        >
          Update
        </SubmitButton>
      </DialogActions>
    </Dialog>
  );
};
